import type { ReactNode } from "react";
import { DownOutlined, LoadingOutlined, UpOutlined } from "@ant-design/icons";

import { ToggleSwitch } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";

export interface FeatureCardProps {
  /** Feature display name shown in the card header. */
  name: string;
  /** Short description rendered beneath the name. */
  description: string;
  /** Operator-facing enable toggle state. */
  enabled: boolean;
  /** Fired with the next enabled value when the header toggle changes. */
  onEnabledChange: (next: boolean) => void;
  /** Whether the collapsible body is open. */
  expanded: boolean;
  /** Fired when the header (or chevron) requests an expand/collapse. */
  onToggleExpand: () => void;
  /** Disables the enable toggle (e.g. while a mutation is in flight). */
  enableDisabled?: boolean;
  /** Shows a spinner beside the toggle while the enable change is persisting. */
  enableLoading?: boolean;
  /** Stable id used to namespace test ids and aria targets. */
  cardId: string;
  /** Collapsible body content — only mounted while expanded. */
  children?: ReactNode;
}

/**
 * Presentational shell for a single configurable feature on the Casino →
 * Configuration → Features page.
 *
 * It owns no feature state of its own — name, description, the enable toggle
 * and the expanded flag are all controlled by the parent. This keeps the shell
 * reusable across sibling feature cards (Big Winners today, more later) while
 * each feature owns its own tuning form in `children`.
 */
function FeatureCard({
  name,
  description,
  enabled,
  onEnabledChange,
  expanded,
  onToggleExpand,
  enableDisabled = false,
  enableLoading = false,
  cardId,
  children,
}: FeatureCardProps) {
  const bodyId = `feature-card-body-${cardId}`;

  return (
    <section
      className={`feature-card ${expanded ? "feature-card--expanded" : ""}`}
      data-testid={`feature-card-${cardId}`}
    >
      <div className="feature-card-header">
        <button
          type="button"
          className="feature-card-header-main"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggleExpand}
          data-testid={`feature-card-toggle-${cardId}`}
        >
          <span className="feature-card-heading">
            <span className="feature-card-name">{name}</span>
            <span className="feature-card-description">{description}</span>
          </span>
        </button>

        <div className="feature-card-header-actions">
          {/*
            Wrapped so a click on the toggle never bubbles up to the header
            button and accidentally expands/collapses the card.
          */}
          <span
            className="feature-card-enable"
            onClick={(e) => e.stopPropagation()}
            data-testid={`feature-card-enable-${cardId}`}
          >
            {enableLoading ? (
              <LoadingOutlined
                className="feature-card-enable-spinner"
                aria-label={AppStrings.CASINO_CONFIG_FEATURES_SAVING}
                data-testid={`feature-card-enable-spinner-${cardId}`}
              />
            ) : null}
            <ToggleSwitch
              value={enabled}
              onChange={onEnabledChange}
              disabled={enableDisabled}
            />
          </span>
          <button
            type="button"
            className="feature-card-chevron"
            aria-label={
              expanded
                ? AppStrings.CASINO_CONFIG_FEATURES_COLLAPSE
                : AppStrings.CASINO_CONFIG_FEATURES_EXPAND
            }
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={onToggleExpand}
            data-testid={`feature-card-chevron-${cardId}`}
          >
            {expanded ? <UpOutlined /> : <DownOutlined />}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="feature-card-body" id={bodyId}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

export default FeatureCard;
